import { Trans, useLingui } from '@lingui/react/macro';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { type VetoActionOutcome, vetoPendingAutonomousAction } from '../lib/api.js';
import { brandsQuery } from '../lib/queries.js';

// [spec 6bfa5da5, slice 2] THE PAGE THE NOTIFICATION ALWAYS CLAIMED TO HAVE.
//
// The heads-up for an autonomous action shipped a `veto` button pointing
// at /autonomous-actions/:id/veto for the life of the feature. That route
// never existed — no file, nothing in routeTree.gen.ts, no chunk in the
// built assets — so the link rendered a not-found page on every channel
// and no notification veto ever worked. fiscalo's adjust_budget 7e2cc103
// executed with vetoed_at null after the founder tapped it.
//
// It lives under /brand/$slug and not at the old flat path on purpose.
// The veto endpoint is brand-scoped, and this tree's beforeLoad already
// resolves the session and the brand — so the page reuses both instead
// of needing a new unscoped endpoint whose authorization would have to
// be kept in step with the one that exists.
//
// WHAT THIS PAGE DELIBERATELY DOES NOT DO: read the action. There is no
// GET for a pending action anywhere in the api, and adding one was the
// surface this route was chosen to avoid. The founder has just read what
// mag is about to do — it is the body of the notification they tapped —
// so the page asks them to confirm rather than re-narrating it. It must
// not invent a description it cannot source.
//
// AND IT NEVER ACTS ON LOAD. A GET that vetoes is a GET that any link
// prefetcher, mail scanner or over-eager browser can fire; the founder
// has to click. That is also why the old dead href was harmless rather
// than dangerous, and the property is worth keeping on the way out.

export const Route = createFileRoute('/brand/$slug/autonomous-actions/$id/veto')({
  component: VetoActionPage,
});

function VetoActionPage() {
  const { slug, id } = useParams({ from: '/brand/$slug/autonomous-actions/$id/veto' });
  const { t } = useLingui();
  const { data: brands } = useQuery(brandsQuery());
  const brand = brands?.find((b) => b.slug === slug);
  const [outcome, setOutcome] = useState<VetoActionOutcome | null>(null);
  const [busy, setBusy] = useState(false);

  async function stopIt() {
    if (!brand || busy) return;
    setBusy(true);
    try {
      setOutcome(await vetoPendingAutonomousAction(brand.id, id, 'stopped from the veto link'));
    } catch {
      // The api helper already folds http errors into an outcome; this
      // catches the network itself falling over. Either way the founder
      // gets a sentence, never a blank page in a window that is closing.
      setOutcome('failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-[560px] px-5 py-10">
      <div className="overflow-hidden rounded-2xl border-[1.5px] border-[var(--ink)] bg-white shadow-[6px_6px_0_var(--plum)]">
        <div className="px-5 pt-5">
          <h1 className="font-display text-[20px] font-bold leading-tight tracking-[-0.01em] text-[var(--ink)]">
            <VetoTitle outcome={outcome} />
          </h1>
          <div className="mt-2 text-[13.5px] leading-[1.55] text-[var(--ink)]/75">
            <VetoBody outcome={outcome} />
          </div>
        </div>
        {/* Actions right-aligned, per the house rule. */}
        <div className="mt-5 flex items-center justify-end gap-3 border-t border-[var(--line)] bg-[var(--paper)] px-5 py-3">
          <Link to="/brand/$slug/home" params={{ slug }} className="link-quiet text-[12px]">
            {outcome === null ? <Trans>leave it running</Trans> : <Trans>back to mag</Trans>}
          </Link>
          {outcome === null ? (
            <button
              type="button"
              onClick={() => void stopIt()}
              disabled={busy || !brand}
              className="rounded-full bg-[var(--pulse)] px-4 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.08em] text-[var(--cream)] hover:opacity-90 disabled:opacity-50"
            >
              {busy ? t`stopping…` : t`stop it`}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** One line per outcome, as a flat switch rather than a ternary chain.
 *  Split out of the page for readability only — every branch is one
 *  <Trans>, so the message ids are unchanged. */
function VetoTitle({ outcome }: { outcome: VetoActionOutcome | null }) {
  switch (outcome) {
    case null:
      return <Trans>stop this one?</Trans>;
    case 'ok':
      return <Trans>stopped it.</Trans>;
    case 'too_late':
      return <Trans>too late.</Trans>;
    case 'not_found':
      return <Trans>can't find it.</Trans>;
    default:
      return <Trans>couldn't stop it.</Trans>;
  }
}

function VetoBody({ outcome }: { outcome: VetoActionOutcome | null }) {
  switch (outcome) {
    case null:
      return (
        <Trans>
          mag is about to make the change she just told you about. stopping it leaves everything
          exactly as it is.
        </Trans>
      );
    case 'ok':
      return <Trans>she won't run it. nothing changed.</Trans>;
    case 'too_late':
      // Not an apology. The window is minutes long by design, and saying
      // "stopped it" here would be the same lie as the button that
      // pointed at nothing.
      return (
        <Trans>
          that one already went through — the window closed before this got here. you can still
          change it back from the campaign.
        </Trans>
      );
    case 'not_found':
      return <Trans>no pending action with that id on this brand.</Trans>;
    default:
      return <Trans>something went wrong on our side. try again, or tell mag in chat.</Trans>;
  }
}
